// TODO: i18n - receive translations parameter for multilingual PDF generation
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Constants
 */
const CRIT_LABELS = { MA: 'Very High', A: 'High', M: 'Moderate', B: 'Low', MB: 'Very Low' };
const CRIT_HEX = { MA: '#dc2626', A: '#f97316', M: '#eab308', B: '#3b82f6', MB: '#22c55e' };
const RISK_HEX = { Critical: '#dc2626', High: '#f97316', Moderate: '#eab308', Low: '#3b82f6', Info: '#94a3b8' };
const VALUATION_HEX = { 5: '#dc2626', 4: '#f97316', 3: '#eab308', 2: '#3b82f6', 1: '#22c55e' };
// Default functions (used as fallback; actual domains derived from data)
const DEFAULT_FUNCTIONS = ['Govern', 'Identify', 'Protect', 'Detect', 'Respond', 'Recover'];

const CRITICALITY_MATRIX = {
  MA: { MB: 'B', B: 'M', M: 'A', A: 'MA', MA: 'MA' },
  A:  { MB: 'B', B: 'M', M: 'A', A: 'A',  MA: 'A' },
  M:  { MB: 'MB', B: 'B', M: 'M', A: 'M',  MA: 'M' },
  B:  { MB: 'MB', B: 'B', M: 'B', A: 'B',  MA: 'B' },
  MB: { MB: 'MB', B: 'MB', M: 'MB', A: 'MB', MA: 'MB' },
};

const DEFAULT_SECTOR_DEPS = {
  energy: [], telecommunications: ['energy'], water: ['energy'],
  financial: ['energy', 'telecommunications'], banking: ['energy', 'telecommunications'],
  health: ['energy', 'telecommunications', 'water'], transportation: ['energy', 'telecommunications'],
  government: ['energy', 'telecommunications'], manufacturing: ['energy', 'telecommunications', 'water'],
};

const SECTOR_HEX = {
  energy: '#eab308', telecommunications: '#3b82f6', water: '#06b6d4',
  financial: '#22c55e', banking: '#22c55e', health: '#ec4899',
  transportation: '#f97316', government: '#8b5cf6', manufacturing: '#64748b',
};

const CRIT_SIZE = { MA: 6, A: 5.2, M: 4.5, B: 3.8, MB: 3 };

function hexToRgb(hex) {
  return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
}

function normalizeSectorKey(sector) {
  if (!sector || sector === '—' || sector === 'Unclassified') return null;
  return sector.toLowerCase().replace(/[\s/-]+/g, '_').replace(/[()]/g, '');
}

/**
 * Generate a comprehensive CII Risk Assessment Report PDF.
 */
export default async function generateFullReportPdf(data, orgOverride, options = {}, labels = {}) {
  const L = {
    headerTitle: 'CII Security Management',
    reportTitle: 'CII Assessment Report',
    reportFilenamePrefix: 'CII',
    platformTitle: 'Critical Information Infrastructure',
    entityName: 'OES',
    entityNamePlural: 'OES',
    brandName: 'LAC4',
    ...labels,
  };
  const org = orgOverride || data.organization || {};
  const isAnonymized = options.anonymized || false;
  const SECTOR_DEPS = data.sector_deps || DEFAULT_SECTOR_DEPS;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 18;
  const contentW = pageW - margin * 2;
  let y = margin;

  // Track sections for TOC
  const tocEntries = [];
  let sectionNum = 0;

  // ── Helpers ──────────────────────────────────────────────────────────
  const addPage = () => { doc.addPage(); y = margin; };
  const checkSpace = (needed) => { if (y + needed > pageH - 20) addPage(); };

  const sectionTitle = (text, level = 1) => {
    checkSpace(16);
    const num = level === 1 ? `${++sectionNum}. ` : '';
    const fontSize = level === 1 ? 14 : level === 2 ? 12 : 10;
    doc.setFontSize(fontSize);
    doc.setTextColor(0, 71, 107); // Dark teal matching reference
    doc.setFont(undefined, 'bold');
    const fullText = `${num}${text}`;
    doc.text(fullText, margin, y);
    if (level === 1) {
      tocEntries.push({ text: fullText, page: doc.internal.getNumberOfPages() });
      y += 2;
      doc.setDrawColor(0, 71, 107);
      doc.setLineWidth(0.8);
      doc.line(margin, y, pageW - margin, y);
    }
    y += level === 1 ? 8 : 6;
    doc.setFont(undefined, 'normal');
  };

  const bodyText = (text) => {
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.setFont(undefined, 'normal');
    const lines = doc.splitTextToSize(text, contentW);
    lines.forEach(line => {
      checkSpace(5);
      doc.text(line, margin, y);
      y += 4;
    });
    y += 2;
  };

  const scoreBar = (x, barY, value, max = 5, width = 40) => {
    const barH = 4;
    doc.setFillColor(230, 230, 230);
    doc.roundedRect(x, barY, width, barH, 1, 1, 'F');
    const pct = Math.min(1, Math.max(0, value / max));
    const color = value >= 4.5 ? hexToRgb('#dc2626') : value >= 3.5 ? hexToRgb('#f97316') : value >= 2.5 ? hexToRgb('#eab308') : value >= 1.5 ? hexToRgb('#3b82f6') : hexToRgb('#22c55e');
    doc.setFillColor(...color);
    doc.roundedRect(x, barY, Math.max(pct * width, 1), barH, 1, 1, 'F');
  };

  /**
   * Draw a NIST CSF radar chart directly in PDF using jsPDF primitives.
   * @param {number} cx - Center X (mm)
   * @param {number} cy - Center Y (mm)
   * @param {number} r - Radius (mm)
   * @param {Object} byDomain - {Govern: {pct}, Identify: {pct}, ...}
   * @param {string} oesName - For label
   */
  const drawRadarChart = (cx, cy, r, byDomain, oesName) => {
    const functions = Object.keys(byDomain).filter(k => byDomain[k] && typeof byDomain[k] === 'object');
    const n = functions.length;
    const angleStep = (2 * Math.PI) / n;
    const startAngle = -Math.PI / 2; // Start from top

    // Helper: get point on radar
    const point = (i, pct) => {
      const angle = startAngle + i * angleStep;
      const dist = (pct / 100) * r;
      return { x: cx + dist * Math.cos(angle), y: cy + dist * Math.sin(angle) };
    };

    // Draw grid rings (20%, 40%, 60%, 80%, 100%)
    [0.2, 0.4, 0.6, 0.8, 1.0].forEach(ring => {
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.15);
      for (let i = 0; i < n; i++) {
        const a1 = startAngle + i * angleStep;
        const a2 = startAngle + ((i + 1) % n) * angleStep;
        const x1 = cx + ring * r * Math.cos(a1);
        const y1 = cy + ring * r * Math.sin(a1);
        const x2 = cx + ring * r * Math.cos(a2);
        const y2 = cy + ring * r * Math.sin(a2);
        doc.line(x1, y1, x2, y2);
      }
    });

    // Draw axis lines
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.2);
    for (let i = 0; i < n; i++) {
      const p = point(i, 100);
      doc.line(cx, cy, p.x, p.y);
    }

    // Draw target line (100% = outer ring) — blue dashed
    doc.setDrawColor(0, 71, 107);
    doc.setLineWidth(0.3);
    for (let i = 0; i < n; i++) {
      const p1 = point(i, 100);
      const p2 = point((i + 1) % n, 100);
      doc.line(p1.x, p1.y, p2.x, p2.y);
    }

    // Draw OES data polygon — filled
    const values = functions.map(fn => (byDomain[fn] || {}).pct || 0);
    doc.setDrawColor(234, 179, 8); // amber
    doc.setLineWidth(0.5);
    doc.setFillColor(234, 179, 8);
    // Draw filled polygon manually
    for (let i = 0; i < n; i++) {
      const p1 = point(i, values[i]);
      const p2 = point((i + 1) % n, values[(i + 1) % n]);
      doc.line(p1.x, p1.y, p2.x, p2.y);
    }
    // Dots at vertices
    values.forEach((val, i) => {
      const p = point(i, val);
      doc.setFillColor(234, 179, 8);
      doc.circle(p.x, p.y, 0.8, 'F');
    });

    // Labels
    doc.setFontSize(6);
    doc.setTextColor(60, 60, 60);
    doc.setFont(undefined, 'normal');
    functions.forEach((fn, i) => {
      const labelDist = r + 5;
      const angle = startAngle + i * angleStep;
      const lx = cx + labelDist * Math.cos(angle);
      const ly = cy + labelDist * Math.sin(angle);
      const align = Math.abs(Math.cos(angle)) < 0.1 ? 'center' : Math.cos(angle) > 0 ? 'left' : 'right';
      doc.text(fn, lx, ly + 1, { align });
    });

    // Title
    doc.setFontSize(7);
    doc.setTextColor(0, 71, 107);
    doc.setFont(undefined, 'bold');
    doc.text(`NIST CSF — ${oesName}`, cx, cy + r + 12, { align: 'center' });
    doc.setFont(undefined, 'normal');
  };

  // ── COVER PAGE ──────────────────────────────────────────────────────
  y = 40;
  // Org logo
  if (org.logo) {
    try {
      const fmt = org.logo.startsWith('data:image/jpeg') ? 'JPEG' : 'PNG';
      doc.addImage(org.logo, fmt, margin, y, 30, 18);
    } catch { /* skip logo */ }
  }
  y = 80;
  doc.setFontSize(24);
  doc.setTextColor(0, 71, 107);
  doc.setFont(undefined, 'bold');
  doc.text('Risk Assessment', pageW / 2, y, { align: 'center' });
  y += 10;
  doc.setFontSize(18);
  doc.text(L.platformTitle, pageW / 2, y, { align: 'center' });
  y += 20;

  // Assessment org logo (if different)
  doc.setFontSize(14);
  doc.setTextColor(60, 60, 60);
  doc.setFont(undefined, 'normal');
  doc.text(org.name || '', pageW / 2, y, { align: 'center' });
  y += 8;
  if (org.country) {
    doc.setFontSize(12);
    doc.text(org.country, pageW / 2, y, { align: 'center' });
  }

  // Confidential notice
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  const confText = `The content of this document is confidential and exclusive property of ${org.name || 'the organization'}. Distribution or duplication without prior authorization is prohibited.`;
  const confLines = doc.splitTextToSize(confText, contentW - 20);
  confLines.forEach((line, i) => {
    doc.text(line, margin + 10, pageH - 30 + i * 4);
  });

  if (isAnonymized) {
    doc.setFontSize(9);
    doc.setTextColor(220, 38, 38);
    doc.text(`Note: ${L.entityName} identities have been anonymized in this report.`, margin, pageH - 15);
  }

  // ── TABLE OF CONTENTS (placeholder — filled at end) ─────────────────
  addPage();
  const tocPageNum = doc.internal.getNumberOfPages();
  y = margin;
  doc.setFontSize(16);
  doc.setTextColor(0, 71, 107);
  doc.setFont(undefined, 'bold');
  doc.text('Table of Contents', margin, y);
  y += 12;
  // We'll fill this later

  // ── SECTION 1: INTRODUCTION ─────────────────────────────────────────
  addPage();
  sectionTitle('Introduction');
  bodyText(
    `This document presents the risk assessment of critical information infrastructure, ` +
    `conducted by ${org.name || 'the managing organization'} with the objective of identifying, quantifying, measuring, and ` +
    `supporting the treatment of cybersecurity risks related to the infrastructure on which ` +
    `essential services depend.`
  );
  bodyText(
    `The assessment covers the essential services operators and critical infrastructure ` +
    `entities identified within the scope, using internationally recognized standards and ` +
    `best practices for risk analysis and cybersecurity management, including NIST 800-30, ` +
    `NIS2 Directive, ISO/IEC 27005, and MAGERIT.`
  );
  bodyText(
    `The main objective is to strengthen the infrastructure, ensuring the continuity and ` +
    `integrity of services considered critical for the social and economic wellbeing of the nation.`
  );

  // ── SECTION 2: SCOPE ────────────────────────────────────────────────
  addPage();
  sectionTitle('Scope');
  bodyText(
    `This risk assessment covers ${data.total_oes || 0} operators of essential services across ` +
    `${(data.sector_distribution || []).length} sectors. The following table lists the essential services ` +
    `and critical infrastructure operators included in the assessment.`
  );

  // Scope table: Sector | Organization | IT/OT Systems | Automation
  const oesDetails = data.oes_details || [];
  if (oesDetails.length > 0) {
    const scopeRows = oesDetails.map(oes => {
      const crit = oes.criticality;
      const itot = crit?.processes?.map(p => p.it_ot_systems || '').filter(Boolean).join(', ') || '—';
      const autoLevel = crit ? (CRIT_LABELS[crit.automation_level?.toUpperCase()] || crit.automation_level || '—') : '—';
      return [oes.sector, oes.oes_name, itot, autoLevel];
    });
    autoTable(doc, {
      startY: y,
      head: [['Sector', 'Organization', 'IT/OT Systems', 'Automation']],
      body: scopeRows,
      margin: { left: margin, right: margin },
      styles: { fontSize: 7.5, cellPadding: 2.5 },
      headStyles: { fillColor: [0, 71, 107], textColor: [255, 255, 255], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      columnStyles: { 0: { cellWidth: 30 }, 1: { cellWidth: 40 }, 2: { cellWidth: 70 } },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // ── SECTION 3: METHODOLOGY ──────────────────────────────────────────
  addPage();
  sectionTitle('Methodology');
  bodyText(
    `The risk assessment follows a structured workflow composed of three main phases: ` +
    `(1) Information Gathering through self-assessment questionnaires, IT/OT automation profiling, ` +
    `and open-source intelligence; (2) Threat Modeling using the ENISA Incident Classification Taxonomy ` +
    `and attack/exploitation probability matrices; and (3) Risk Estimation using qualitative and ` +
    `semi-quantitative scales aligned with NIST 800-30.`
  );

  // Impact factors table
  checkSpace(50);
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  doc.text('Impact estimation factors (1-5 scale):', margin, y);
  y += 4;
  autoTable(doc, {
    startY: y,
    head: [['Factor', 'Description', '1 (Very Low)', '2 (Low)', '3 (Moderate)', '4 (High)', '5 (Very High)']],
    body: [
      ['Reach/Scope', 'Users or proportion of total users', '0-20%', '20-40%', '40-60%', '60-80%', '80-100%'],
      ['Frequency', 'Frequency of service use', 'Rarely', 'Occasionally', 'Daily', 'Frequently', 'Continuously'],
      ['Replaceability', 'Time to replace service', 'Immediately', 'Hours', '24-72 hours', '>72 hours', 'Not replaceable'],
      ['Interdependency', 'Impact on other essential services', '<1 service', '2 services', '3 services', '4 services', '5+ services'],
      ['Time to Impact', 'Max disruption before significant impact', '>72 hours', '48-72 hours', '24-48 hours', '4-24 hours', '<4 hours'],
    ],
    margin: { left: margin, right: margin },
    styles: { fontSize: 6, cellPadding: 2 },
    headStyles: { fillColor: [0, 71, 107], textColor: [255, 255, 255], fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 247, 250] },
  });
  y = doc.lastAutoTable.finalY + 6;

  // Automation scale
  checkSpace(30);
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  doc.text('IT/OT Automation scale (1-5):', margin, y);
  y += 4;
  autoTable(doc, {
    startY: y,
    head: [['Factor', 'Description', '1 (Very Low)', '2 (Low)', '3 (Moderate)', '4 (High)', '5 (Very High)']],
    body: [
      ['Automation', 'IT/OT dependency level', 'Fully manual', 'Low automation, manual fallback', 'Moderate automation, manual feasible', 'Mostly automated, manual contingency', 'Fully automated, no manual fallback'],
    ],
    margin: { left: margin, right: margin },
    styles: { fontSize: 6, cellPadding: 2 },
    headStyles: { fillColor: [0, 71, 107], textColor: [255, 255, 255], fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [245, 247, 250] },
  });
  y = doc.lastAutoTable.finalY + 6;

  // Criticality matrix reference
  checkSpace(50);
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  doc.text(`Criticality matrix (Impact × Automation = ${L.entityName} Criticality):`, margin, y);
  y += 4;
  const matrixRows = ['MA', 'A', 'M', 'B', 'MB'].map(row => {
    return [CRIT_LABELS[row], ...['MB', 'B', 'M', 'A', 'MA'].map(col => CRIT_LABELS[CRITICALITY_MATRIX[row][col]])];
  });
  autoTable(doc, {
    startY: y,
    head: [['Impact \\ Automation', ...['MB', 'B', 'M', 'A', 'MA'].map(k => CRIT_LABELS[k])]],
    body: matrixRows,
    margin: { left: margin, right: margin },
    styles: { fontSize: 7, cellPadding: 2.5, halign: 'center' },
    headStyles: { fillColor: [0, 71, 107], textColor: [255, 255, 255], fontStyle: 'bold' },
    didParseCell: (hookData) => {
      if (hookData.section === 'body' && hookData.column.index > 0) {
        const val = hookData.cell.raw;
        const keyMap = { 'Very High': 'MA', 'High': 'A', 'Moderate': 'M', 'Low': 'B', 'Very Low': 'MB' };
        const key = keyMap[val];
        const colors = { MA: [254, 202, 202], A: [254, 215, 170], M: [253, 230, 138], B: [191, 219, 254], MB: [187, 247, 208] };
        if (key && colors[key]) hookData.cell.styles.fillColor = colors[key];
      }
    },
  });
  y = doc.lastAutoTable.finalY + 8;

  // ── SECTION 4: THREAT MODELING ──────────────────────────────────────
  addPage();
  sectionTitle('Threat Modeling');
  bodyText(
    `Threat modeling is based on the ENISA Reference Incident Classification Taxonomy. ` +
    `Attack and exploitation probability estimates consider environmental and institution-specific ` +
    `factors including historical incidents, sector criticality, geopolitical context, ` +
    `legal framework strength, and vulnerability exploitability.`
  );

  // Threat table from top_threats
  const threats = data.top_threats || [];
  if (threats.length > 0) {
    autoTable(doc, {
      startY: y,
      head: [['Threat', 'Max Risk Score']],
      body: threats.map(t => [t.threat_name, t.max_risk?.toFixed(1) || '0']),
      margin: { left: margin, right: margin },
      styles: { fontSize: 8, cellPadding: 2.5 },
      headStyles: { fillColor: [0, 71, 107], textColor: [255, 255, 255], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 247, 250] },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // ── SECTION 5: OES CRITICALITY PROFILES ─────────────────────────────
  addPage();
  sectionTitle(`${L.entityName} Criticality Profiles`);
  bodyText(
    `Each operator of essential services is classified based on impact factors and IT/OT ` +
    `automation dependency. Only operators achieving a criticality level of High or Very High ` +
    `are considered critical information infrastructure.`
  );

  // Group OESes by sector
  const bySector = {};
  oesDetails.forEach(oes => {
    const sector = oes.sector || 'Other';
    if (!bySector[sector]) bySector[sector] = [];
    bySector[sector].push(oes);
  });

  Object.entries(bySector).forEach(([sector, oeses]) => {
    checkSpace(30);
    doc.setFontSize(10);
    doc.setTextColor(0, 71, 107);
    doc.setFont(undefined, 'bold');
    doc.text(sector, margin, y);
    y += 6;
    doc.setFont(undefined, 'normal');

    oeses.forEach((oes, oesIdx) => {
      checkSpace(55);
      const crit = oes.criticality;

      // Subtle separator between OESes
      if (oesIdx > 0) {
        doc.setDrawColor(210, 215, 220);
        doc.setLineWidth(0.2);
        doc.line(margin + 2, y - 2, pageW - margin - 2, y - 2);
        y += 2;
      }

      // OES name + criticality badge
      doc.setFontSize(9);
      doc.setTextColor(30, 41, 59);
      doc.setFont(undefined, 'bold');
      doc.text(oes.oes_name, margin + 2, y);
      if (crit) {
        const level = crit.oes_criticality?.toUpperCase();
        const label = CRIT_LABELS[level] || level;
        doc.setFontSize(9);
        const badgeX = margin + 2 + doc.getTextWidth(oes.oes_name) + 8;
        doc.setFontSize(7);
        const labelW = doc.getTextWidth(label) + 4;
        const color = hexToRgb(CRIT_HEX[level] || '#94a3b8');
        doc.setFillColor(...color);
        doc.roundedRect(badgeX, y - 3, labelW, 4.5, 1, 1, 'F');
        doc.setTextColor(255, 255, 255);
        doc.text(label, badgeX + 2, y);
      }
      y += 6;

      if (crit) {
        doc.setFont(undefined, 'normal');
        const barStart = margin + 50;
        const barW = 35;

        // Criticality, Impact, Automation summary bars
        doc.setFontSize(7);
        doc.setTextColor(30, 41, 59);
        doc.setFont(undefined, 'bold');
        doc.text('CRITICALITY', margin + 2, y + 3);
        scoreBar(barStart, y, { MA: 5, A: 4, M: 3, B: 2, MB: 1 }[crit.oes_criticality?.toUpperCase()] || 3, 5, barW);
        y += 6;
        doc.text('Impact', margin + 2, y + 3);
        scoreBar(barStart, y, crit.impact_average || 0, 5, barW);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(6);
        doc.setTextColor(120, 120, 120);
        doc.text(`${(crit.impact_average || 0).toFixed(1)}`, barStart + barW + 3, y + 3);
        y += 5;

        // Impact sub-factors
        const impactFactors = [
          ['  Reach/Scope', crit.alcance_score],
          ['  Frequency', crit.frecuencia_score],
          ['  Irreplaceability', crit.reemplazabilidad_score],
          ['  Interdependency', crit.interdependencia_score],
          ['  Disruption Speed', crit.continuidad_score],
        ];
        impactFactors.forEach(([label, val]) => {
          doc.setFontSize(6);
          doc.setTextColor(100, 100, 100);
          doc.text(label, margin + 2, y + 3);
          scoreBar(barStart, y, val || 0, 5, barW);
          y += 4.5;
        });
        y += 2;

        // Automation
        doc.setFontSize(7);
        doc.setTextColor(30, 41, 59);
        doc.setFont(undefined, 'bold');
        doc.text('IT/OT Automation', margin + 2, y + 3);
        scoreBar(barStart, y, crit.automation_score || 0, 5, barW);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(6);
        doc.setTextColor(120, 120, 120);
        doc.text(`${(crit.automation_score || 0).toFixed(1)}`, barStart + barW + 3, y + 3);
        y += 8;
      } else {
        doc.setFontSize(7);
        doc.setTextColor(120, 120, 120);
        doc.text('Criticality not assessed', margin + 2, y);
        y += 5;
      }
    });
    y += 4;
  });

  // ── Criticality Matrix Heatmap (all OESes in one matrix) ──
  const allAssessedOeses = oesDetails.filter(o => o.criticality);
  if (allAssessedOeses.length > 0) {
    addPage();
    doc.setFontSize(11);
    doc.setTextColor(0, 71, 107);
    doc.setFont(undefined, 'bold');
    doc.text(`Criticality Matrix — Assessed ${L.entityNamePlural} by Quadrant`, margin, y);
    y += 3;
    doc.setDrawColor(0, 71, 107);
    doc.setLineWidth(0.4);
    doc.line(margin, y, pageW - margin, y);
    y += 8;
    doc.setFont(undefined, 'normal');

    // Axis order: left-to-right = MB to MA (Very Low to Very High) — matches dashboard
    const autCols = ['MB', 'B', 'M', 'A', 'MA']; // Automation left-to-right
    const impRows = ['MA', 'A', 'M', 'B', 'MB']; // Impact top-to-bottom
    const cellW = (contentW - 22) / 5;
    const cellH = 18;
    const matrixX = margin + 22;
    const matrixY = y;
    const matrixColors = {
      MA: [252, 165, 165], A: [253, 186, 116], M: [252, 211, 77], B: [147, 197, 253], MB: [134, 239, 172],
    };

    // Build OES-per-cell lookup
    const cellOeses = {};
    impRows.forEach(row => {
      autCols.forEach(col => {
        const key = `${row}-${col}`;
        cellOeses[key] = allAssessedOeses.filter(o => {
          const impLvl = o.criticality?.impact_level?.toUpperCase();
          const autLvl = o.criticality?.automation_level?.toUpperCase();
          return impLvl === row && autLvl === col;
        });
      });
    });

    // Header row (Automation axis)
    doc.setFontSize(6);
    doc.setTextColor(80, 80, 80);
    autCols.forEach((col, ci) => {
      const cx = matrixX + ci * cellW;
      doc.setFillColor(235, 238, 242);
      doc.rect(cx, matrixY, cellW, 7, 'F');
      doc.setDrawColor(210, 215, 220);
      doc.setLineWidth(0.2);
      doc.rect(cx, matrixY, cellW, 7, 'S');
      doc.setTextColor(60, 60, 60);
      doc.text(CRIT_LABELS[col], cx + cellW / 2, matrixY + 4.5, { align: 'center' });
    });

    // Impact row headers + cells
    impRows.forEach((row, ri) => {
      const gy = matrixY + 7 + ri * cellH;
      // Row header
      doc.setFillColor(235, 238, 242);
      doc.rect(matrixX - 22, gy, 22, cellH, 'F');
      doc.setDrawColor(210, 215, 220);
      doc.setLineWidth(0.2);
      doc.rect(matrixX - 22, gy, 22, cellH, 'S');
      doc.setFontSize(6);
      doc.setTextColor(60, 60, 60);
      doc.text(CRIT_LABELS[row], matrixX - 11, gy + cellH / 2 + 1, { align: 'center' });

      autCols.forEach((col, ci) => {
        const cx = matrixX + ci * cellW;
        const critResult = CRITICALITY_MATRIX[row]?.[col] || '';
        const rgb = matrixColors[critResult] || [240, 240, 240];
        doc.setFillColor(...rgb);
        doc.rect(cx, gy, cellW, cellH, 'F');
        doc.setDrawColor(230, 230, 230);
        doc.setLineWidth(0.15);
        doc.rect(cx, gy, cellW, cellH, 'S');

        // Place OES names in cell
        const oesInCell = cellOeses[`${row}-${col}`] || [];
        if (oesInCell.length > 0) {
          const maxFit = Math.floor(cellH / 3.5);
          doc.setFontSize(4.5);
          doc.setTextColor(30, 30, 30);
          doc.setFont(undefined, 'bold');
          oesInCell.slice(0, maxFit).forEach((o, oi) => {
            const displayName = o.oes_name.length > 18 ? o.oes_name.substring(0, 16) + '..' : o.oes_name;
            doc.text(displayName, cx + cellW / 2, gy + 3.5 + oi * 3.5, { align: 'center' });
          });
          if (oesInCell.length > maxFit) {
            doc.setFontSize(4);
            doc.setTextColor(100, 100, 100);
            doc.text(`+${oesInCell.length - maxFit} more`, cx + cellW / 2, gy + cellH - 1, { align: 'center' });
          }
          doc.setFont(undefined, 'normal');
        }
      });
    });

    // Axis labels
    doc.setFontSize(6);
    doc.setTextColor(80, 80, 80);
    doc.setFont(undefined, 'bold');
    doc.text('AUTOMATION', matrixX + (5 * cellW) / 2, matrixY + 7 + impRows.length * cellH + 4, { align: 'center' });
    // Impact vertical label
    const impMidY = matrixY + 7 + (impRows.length * cellH) / 2;
    doc.text('IMPACT', matrixX - 22 - 3, impMidY, { angle: 90 });
    doc.setFont(undefined, 'normal');

    y = matrixY + 7 + impRows.length * cellH + 10;
  }

  // ── SECTION 6: PER-OES RISK PROFILES ────────────────────────────────
  let oesNum = 0;
  oesDetails.forEach(oes => {
    oesNum++;
    addPage();
    sectionNum--; // Don't increment main section counter for each OES
    const subsectionBase = `6.${oesNum}`;

    // OES Header
    doc.setFontSize(14);
    doc.setTextColor(0, 71, 107);
    doc.setFont(undefined, 'bold');
    doc.text(`${subsectionBase} ${oes.oes_name}`, margin, y);
    tocEntries.push({ text: `${subsectionBase} ${oes.oes_name}`, page: doc.internal.getNumberOfPages() });
    y += 3;
    doc.setDrawColor(0, 71, 107);
    doc.setLineWidth(0.4);
    doc.line(margin, y, pageW - margin, y);
    y += 8;
    doc.setFont(undefined, 'normal');

    const crit = oes.criticality;
    const posture = oes.posture;
    const riskMatrix = oes.risk_matrix;
    const vulns = oes.vulnerabilities || [];

    // 6.x.1 Asset Valuation
    doc.setFontSize(11);
    doc.setTextColor(0, 71, 107);
    doc.setFont(undefined, 'bold');
    doc.text(`${subsectionBase}.1 Asset Valuation`, margin, y);
    y += 6;
    doc.setFont(undefined, 'normal');

    const assetValuations = crit?.asset_valuations || {};
    const assetEntries = Object.entries(assetValuations);
    if (assetEntries.length > 0) {
      autoTable(doc, {
        startY: y,
        head: [['IT/OT System', 'Valuation']],
        body: assetEntries.map(([name, val]) => [name, `${CRIT_LABELS[['', 'MB', 'B', 'M', 'A', 'MA'][val]] || val} (${val})`]),
        margin: { left: margin, right: margin },
        styles: { fontSize: 8, cellPadding: 2.5 },
        headStyles: { fillColor: [0, 71, 107], textColor: [255, 255, 255], fontStyle: 'bold' },
        didParseCell: (hookData) => {
          if (hookData.section === 'body' && hookData.column.index === 1) {
            const val = assetEntries[hookData.row.index]?.[1];
            const colors = { 5: [254, 202, 202], 4: [254, 215, 170], 3: [253, 230, 138], 2: [191, 219, 254], 1: [187, 247, 208] };
            if (colors[val]) hookData.cell.styles.fillColor = colors[val];
          }
        },
      });
      y = doc.lastAutoTable.finalY + 8;
    } else {
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text('No asset valuations available.', margin, y);
      y += 6;
    }

    // 6.x.2.1 NIST CSF Evaluation
    checkSpace(30);
    doc.setFontSize(11);
    doc.setTextColor(0, 71, 107);
    doc.setFont(undefined, 'bold');
    doc.text(`${subsectionBase}.2 Risk Profile`, margin, y);
    y += 6;
    doc.setFontSize(10);
    doc.text(`${subsectionBase}.2.1 Cybersecurity Framework Evaluation`, margin, y);
    y += 6;
    doc.setFont(undefined, 'normal');

    if (posture && posture.by_domain) {
      const byDomain = posture.by_domain;

      // Radar chart
      checkSpace(75);
      drawRadarChart(pageW / 2, y + 25, 22, byDomain, oes.oes_name);
      y += 68;

      const domainKeys = Object.keys(byDomain).filter(k => byDomain[k] && typeof byDomain[k] === 'object');
      autoTable(doc, {
        startY: y,
        head: [['Domain', 'Compliance (%)', 'Assessed', 'Total']],
        body: domainKeys.map(fn => {
          const d = byDomain[fn] || {};
          return [fn, `${d.pct || 0}%`, d.assessed || 0, d.total || 0];
        }),
        margin: { left: margin, right: margin },
        styles: { fontSize: 8, cellPadding: 2.5 },
        headStyles: { fillColor: [0, 71, 107], textColor: [255, 255, 255], fontStyle: 'bold' },
        didParseCell: (hookData) => {
          if (hookData.section === 'body' && hookData.column.index === 1) {
            const fn = domainKeys[hookData.row.index];
            const pct = (byDomain[fn] || {}).pct || 0;
            if (pct >= 75) hookData.cell.styles.fillColor = [187, 247, 208];
            else if (pct >= 50) hookData.cell.styles.fillColor = [253, 230, 138];
            else hookData.cell.styles.fillColor = [254, 202, 202];
          }
        },
      });
      y = doc.lastAutoTable.finalY + 4;
      doc.setFontSize(8);
      doc.setTextColor(60, 60, 60);
      doc.text(`Overall compliance: ${posture.gen_compliance || 0}%`, margin, y);
      y += 6;
    } else {
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text('No posture assessment available.', margin, y);
      y += 6;
    }

    // Findings (vulnerabilities)
    if (vulns.length > 0) {
      checkSpace(20);
      doc.setFontSize(9);
      doc.setTextColor(0, 71, 107);
      doc.setFont(undefined, 'bold');
      doc.text('Findings:', margin, y);
      y += 4;
      doc.setFont(undefined, 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(60, 60, 60);
      vulns.slice(0, 15).forEach(v => {
        checkSpace(5);
        doc.text(`• ${v.vulnerability_title}`, margin + 2, y);
        y += 4;
      });
      if (vulns.length > 15) {
        doc.text(`... and ${vulns.length - 15} more`, margin + 2, y);
        y += 4;
      }
      y += 4;
    }

    // 6.x.2.2 Risk Matrix
    if (riskMatrix && riskMatrix.rows && riskMatrix.rows.length > 0) {
      checkSpace(55);
      doc.setFontSize(10);
      doc.setTextColor(0, 71, 107);
      doc.setFont(undefined, 'bold');
      doc.text(`${subsectionBase}.2.2 Risk Matrix`, margin, y);

      // ── Risk gauge (semi-circular speedometer) — positioned at title level ──
      const overallClass = riskMatrix.overall_classification || 'Info';
      const overallScore = riskMatrix.overall_max_risk_score || 0;
      const gaugeCx = pageW - margin - 22;
      const gaugeCy = y - 2;
      const gaugeR = 14;

      // Draw arc segments: Info → Low → Moderate → High → Critical
      const segments = [
        { label: 'Info', color: [148, 163, 184], start: Math.PI, end: Math.PI + Math.PI * 0.2 },
        { label: 'Low', color: [56, 189, 248], start: Math.PI + Math.PI * 0.2, end: Math.PI + Math.PI * 0.4 },
        { label: 'Moderate', color: [250, 204, 21], start: Math.PI + Math.PI * 0.4, end: Math.PI + Math.PI * 0.6 },
        { label: 'High', color: [251, 146, 60], start: Math.PI + Math.PI * 0.6, end: Math.PI + Math.PI * 0.8 },
        { label: 'Critical', color: [220, 38, 38], start: Math.PI + Math.PI * 0.8, end: 2 * Math.PI },
      ];

      segments.forEach(seg => {
        doc.setFillColor(...seg.color);
        // Draw arc as a series of small triangles
        const steps = 12;
        const stepAngle = (seg.end - seg.start) / steps;
        for (let s = 0; s < steps; s++) {
          const a1 = seg.start + s * stepAngle;
          const a2 = seg.start + (s + 1) * stepAngle;
          const innerR = gaugeR * 0.55;
          // Draw quad as two triangles
          const x1o = gaugeCx + gaugeR * Math.cos(a1), y1o = gaugeCy + gaugeR * Math.sin(a1);
          const x2o = gaugeCx + gaugeR * Math.cos(a2), y2o = gaugeCy + gaugeR * Math.sin(a2);
          const x1i = gaugeCx + innerR * Math.cos(a1), y1i = gaugeCy + innerR * Math.sin(a1);
          const x2i = gaugeCx + innerR * Math.cos(a2), y2i = gaugeCy + innerR * Math.sin(a2);
          doc.triangle(x1o, y1o, x2o, y2o, x1i, y1i, 'F');
          doc.triangle(x2o, y2o, x2i, y2i, x1i, y1i, 'F');
        }
      });

      // Needle: map score (0-25) to angle (PI to 2*PI)
      const needleAngle = Math.PI + (Math.min(overallScore, 25) / 25) * Math.PI;
      const needleLen = gaugeR * 0.8;
      doc.setDrawColor(50, 50, 50);
      doc.setLineWidth(0.6);
      doc.line(gaugeCx, gaugeCy, gaugeCx + needleLen * Math.cos(needleAngle), gaugeCy + needleLen * Math.sin(needleAngle));
      // Center dot
      doc.setFillColor(50, 50, 50);
      doc.circle(gaugeCx, gaugeCy, 1, 'F');

      // Labels
      doc.setFontSize(5);
      doc.setTextColor(140, 140, 140);
      doc.text('Info', gaugeCx - gaugeR - 2, gaugeCy + 3, { align: 'right' });
      doc.text('Critical', gaugeCx + gaugeR + 2, gaugeCy + 3);

      // Overall label below gauge
      doc.setFontSize(8);
      const overallColor = hexToRgb(RISK_HEX[overallClass] || '#94a3b8');
      doc.setTextColor(...overallColor);
      doc.setFont(undefined, 'bold');
      doc.text(overallClass, gaugeCx, gaugeCy + 7, { align: 'center' });

      // "Overall risk" title above gauge
      doc.setFontSize(6);
      doc.setTextColor(100, 100, 100);
      doc.setFont(undefined, 'normal');
      doc.text('Overall risk', gaugeCx, gaugeCy - gaugeR - 2, { align: 'center' });

      // Advance y past the gauge area
      y = Math.max(y + 8, gaugeCy + 12);

      // ── Risk matrix table ──
      // Group rows by asset, deduplicate threats (one row per threat per asset)
      const byAsset = {};
      riskMatrix.rows.forEach(r => {
        if (!byAsset[r.asset_name]) byAsset[r.asset_name] = { valuation: r.valuation, threats: {} };
        const key = r.threat_name || r.threat_id;
        if (!byAsset[r.asset_name].threats[key]) {
          byAsset[r.asset_name].threats[key] = {
            threat_name: r.threat_name,
            likelihood: r.risk_x_threat || 0,
            risk: r.risk_x_asset || 0,
            classification: r.classification || '—',
            treatment: r.treatment || '—',
            count: 1,
          };
        } else {
          const existing = byAsset[r.asset_name].threats[key];
          // Aggregate: keep max likelihood and risk across vulnerabilities
          existing.likelihood = Math.max(existing.likelihood, r.risk_x_threat || 0);
          existing.risk = Math.max(existing.risk, r.risk_x_asset || 0);
          existing.count++;
          const classOrder = { Critical: 5, High: 4, Moderate: 3, Low: 2, Info: 1 };
          if ((classOrder[r.classification] || 0) > (classOrder[existing.classification] || 0)) {
            existing.classification = r.classification;
            existing.treatment = r.treatment;
          }
        }
      });

      // Build flat table rows
      const LIKELIHOOD_LABELS = { 1: 'Very Low', 2: 'Low', 3: 'Moderate', 4: 'High', 5: 'Very High' };
      const allTableRows = [];
      const assetMeta = []; // parallel array for color lookup

      Object.entries(byAsset).forEach(([assetName, { valuation, threats }]) => {
        const threatRows = Object.values(threats);
        // Compute Risk×Asset = overall risk for the asset (max across threats)
        const riskXAsset = Math.max(...threatRows.map(t => t.risk));
        // Classify the asset overall
        const assetClassification = riskXAsset >= 20 ? 'Critical' : riskXAsset >= 15 ? 'High' : riskXAsset >= 10 ? 'Moderate' : riskXAsset >= 5 ? 'Low' : 'Info';

        threatRows.forEach((t, i) => {
          const likelihoodLabel = LIKELIHOOD_LABELS[Math.round(t.likelihood)] || t.likelihood.toFixed(1);
          allTableRows.push([
            i === 0 ? assetName : '',
            i === 0 ? valuation : '',
            t.threat_name || '—',
            likelihoodLabel,
            i === 0 ? riskXAsset.toFixed(0) : '',
            i === 0 ? assetClassification : '',
            t.treatment || '—',
          ]);
          assetMeta.push({ valuation, isFirstRow: i === 0, assetClassification });
        });
      });

      autoTable(doc, {
        startY: y,
        head: [['Asset', 'Valuation', 'Threat', 'Likelihood', 'Risk', 'Classification', 'Treatment']],
        body: allTableRows,
        margin: { left: margin, right: margin },
        styles: { fontSize: 6.5, cellPadding: 1.5 },
        headStyles: { fillColor: [0, 71, 107], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 6.5 },
        columnStyles: { 0: { cellWidth: 28 }, 1: { cellWidth: 15 }, 4: { cellWidth: 12 } },
        didParseCell: (hookData) => {
          if (hookData.section === 'body') {
            const meta = assetMeta[hookData.row.index];
            if (!meta) return;
            // Color asset + valuation cells on first row
            if (meta.isFirstRow && hookData.column.index <= 1) {
              const colors = { 5: [254, 202, 202], 4: [254, 215, 170], 3: [253, 230, 138], 2: [191, 219, 254], 1: [187, 247, 208] };
              if (colors[meta.valuation]) hookData.cell.styles.fillColor = colors[meta.valuation];
            }
            // Color classification column
            if (hookData.column.index === 5 && meta.isFirstRow) {
              const riskColors = { Critical: [254, 202, 202], High: [254, 215, 170], Moderate: [253, 230, 138], Low: [191, 219, 254], Info: [226, 232, 240] };
              if (riskColors[meta.assetClassification]) hookData.cell.styles.fillColor = riskColors[meta.assetClassification];
            }
          }
        },
      });
      y = doc.lastAutoTable.finalY + 6;
    }
  });
  // Restore section counter for remaining sections
  sectionNum = 6;

  // ── SECTION 7: INTERDEPENDENCY ANALYSIS ─────────────────────────────
  addPage();
  sectionTitle('Interdependency Analysis');
  bodyText(
    `The following table summarizes the interdependency levels of assessed operators. ` +
    `Interdependency measures the potential impact of service disruption on other essential services. ` +
    `A detailed interactive dependency network visualization is available in the platform's Reports section.`
  );

  if (false) { // Dependency graph disabled — rendering deferred to future version
    checkSpace(90);
    const graphCx = pageW / 2;
    const graphCy = y + 38;
    const graphR = 32;

    // Build nodes with circular layout
    const graphNodes = oesDetails.map((oes, i) => {
      const angle = -Math.PI / 2 + (i / oesDetails.length) * 2 * Math.PI;
      const sk = normalizeSectorKey(oes.sector);
      const critLevel = oes.criticality?.oes_criticality?.toUpperCase() || 'M';
      return {
        x: graphCx + graphR * Math.cos(angle),
        y: graphCy + graphR * Math.sin(angle),
        name: oes.oes_name,
        sectorKey: sk,
        r: CRIT_SIZE[critLevel] || 4,
        color: SECTOR_HEX[sk] || '#94a3b8',
      };
    });

    // Draw links (arrows)
    graphNodes.forEach((nodeA, i) => {
      graphNodes.forEach((nodeB, j) => {
        if (i === j) return;
        const aDeps = SECTOR_DEPS[nodeA.sectorKey] || [];
        if (aDeps.includes(nodeB.sectorKey)) {
          // nodeA depends on nodeB — draw arrow from A to B
          doc.setDrawColor(180, 180, 180);
          doc.setLineWidth(0.3);
          doc.line(nodeA.x, nodeA.y, nodeB.x, nodeB.y);
          // Arrowhead
          const dx = nodeB.x - nodeA.x;
          const dy = nodeB.y - nodeA.y;
          const len = Math.sqrt(dx * dx + dy * dy);
          if (len > 0) {
            const nx = dx / len, ny = dy / len;
            const arrowLen = 2;
            const tipX = nodeB.x - nx * (nodeB.r + 1.5);
            const tipY = nodeB.y - ny * (nodeB.r + 1.5);
            doc.setFillColor(150, 150, 150);
            doc.triangle(
              tipX, tipY,
              tipX - nx * arrowLen + ny * arrowLen * 0.4, tipY - ny * arrowLen - nx * arrowLen * 0.4,
              tipX - nx * arrowLen - ny * arrowLen * 0.4, tipY - ny * arrowLen + nx * arrowLen * 0.4,
              'F'
            );
          }
        }
      });
    });

    // Draw nodes (white fill + colored ring)
    graphNodes.forEach(node => {
      const rgb = hexToRgb(node.color);
      // Ring
      doc.setDrawColor(...rgb);
      doc.setLineWidth(1);
      doc.setFillColor(255, 255, 255);
      doc.circle(node.x, node.y, node.r, 'FD');
      // Initial letter
      doc.setFontSize(Math.max(5, node.r * 0.8));
      doc.setTextColor(...rgb);
      doc.setFont(undefined, 'bold');
      doc.text(node.name.charAt(0).toUpperCase(), node.x, node.y + node.r * 0.3, { align: 'center' });
    });

    // Labels outside nodes
    doc.setFont(undefined, 'normal');
    doc.setFontSize(5);
    doc.setTextColor(60, 60, 60);
    graphNodes.forEach((node, i) => {
      const angle = -Math.PI / 2 + (i / graphNodes.length) * 2 * Math.PI;
      const labelDist = node.r + 4;
      const lx = node.x + labelDist * Math.cos(angle);
      const ly = node.y + labelDist * Math.sin(angle);
      const align = Math.cos(angle) > 0.3 ? 'left' : Math.cos(angle) < -0.3 ? 'right' : 'center';
      doc.text(node.name.length > 20 ? node.name.substring(0, 18) + '...' : node.name, lx, ly + 1, { align });
    });

    // Sector legend
    const uniqueSectors = [...new Set(graphNodes.map(n => n.sectorKey).filter(Boolean))];
    doc.setFontSize(5);
    const legendY = graphCy + graphR + 12;
    let legendX = margin;
    uniqueSectors.forEach(sk => {
      const rgb = hexToRgb(SECTOR_HEX[sk] || '#94a3b8');
      doc.setFillColor(...rgb);
      doc.circle(legendX + 1.5, legendY, 1.5, 'F');
      doc.setTextColor(80, 80, 80);
      doc.text(sk.replace(/_/g, ' '), legendX + 4, legendY + 1);
      legendX += doc.getTextWidth(sk.replace(/_/g, ' ')) + 8;
    });

    y = legendY + 8;
  }

  // Interdependency table
  bodyText(
    `The following table summarizes the interdependency levels and criticality of assessed operators.`
  );
  const interdepRows = oesDetails
    .filter(oes => oes.criticality?.interdependencia_score)
    .sort((a, b) => (b.criticality?.interdependencia_score || 0) - (a.criticality?.interdependencia_score || 0))
    .map(oes => [
      oes.oes_name,
      oes.sector,
      oes.criticality?.interdependencia_score || '—',
      CRIT_LABELS[oes.criticality?.oes_criticality?.toUpperCase()] || '—',
    ]);

  if (interdepRows.length > 0) {
    autoTable(doc, {
      startY: y,
      head: [['Organization', 'Sector', 'Interdependency (1-5)', 'Criticality']],
      body: interdepRows,
      margin: { left: margin, right: margin },
      styles: { fontSize: 8, cellPadding: 2.5 },
      headStyles: { fillColor: [0, 71, 107], textColor: [255, 255, 255], fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 247, 250] },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  sectionTitle('Sector Assessment Summary');

  const avgByFn = data.average_by_function || {};
  const avgFnKeys = Object.keys(avgByFn);
  if (avgFnKeys.length > 0) {
    bodyText('Average compliance by framework domain across all assessed operators:');
    autoTable(doc, {
      startY: y,
      head: [['Domain', 'Average Compliance (%)']],
      body: avgFnKeys.map(fn => [fn, `${avgByFn[fn] || 0}%`]),
      margin: { left: margin, right: margin },
      styles: { fontSize: 8, cellPadding: 2.5 },
      headStyles: { fillColor: [0, 71, 107], textColor: [255, 255, 255], fontStyle: 'bold' },
      didParseCell: (hookData) => {
        if (hookData.section === 'body' && hookData.column.index === 1) {
          const fn = avgFnKeys[hookData.row.index];
          const pct = avgByFn[fn] || 0;
          if (pct >= 75) hookData.cell.styles.fillColor = [187, 247, 208];
          else if (pct >= 50) hookData.cell.styles.fillColor = [253, 230, 138];
          else hookData.cell.styles.fillColor = [254, 202, 202];
        }
      },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // ── SECTION 8: CONSIDERATIONS ───────────────────────────────────────
  addPage();
  sectionTitle('Considerations');
  bodyText(
    `The final product of the risk analysis consists of a detailed report of the risk assessment ` +
    `process and its results, which should be reviewed, approved, communicated, and monitored ` +
    `according to current risk management practices and applicable regulatory requirements.`
  );
  bodyText(
    `Risk treatment should be accompanied by specific actions to mitigate, accept, transfer, ` +
    `or avoid the risk, making use of available resources and applicable control frameworks.`
  );

  // ── SECTION 9: REFERENCES ──────────────────────────────────────────
  addPage();
  sectionTitle('References');
  const refs = [
    'NIST SP 800-30 Rev. 1: Guide for Conducting Risk Assessments',
    'MAGERIT v3: Methodology for Information Systems Risk Analysis and Management',
    'ISO/IEC 27005:2018: Information Security Risk Management',
    'Directive (EU) 2022/2555 (NIS2 Directive)',
    'ENISA Reference Incident Classification Taxonomy',
    'NIST Cybersecurity Framework 2.0',
  ];
  doc.setFontSize(8);
  doc.setTextColor(60, 60, 60);
  refs.forEach((ref, i) => {
    doc.text(`${i + 1}. ${ref}`, margin + 2, y);
    y += 5;
  });

  // ── FILL TABLE OF CONTENTS ─────────────────────────────────────────
  doc.setPage(tocPageNum);
  let tocY = margin + 14;
  doc.setFontSize(9);
  doc.setTextColor(60, 60, 60);
  doc.setFont(undefined, 'normal');
  tocEntries.forEach(entry => {
    if (tocY > pageH - 20) return; // safety
    const pageText = `${entry.page}`;
    doc.text(entry.text, margin, tocY);
    doc.text(pageText, pageW - margin, tocY, { align: 'right' });
    // Dotted line
    const textW = doc.getTextWidth(entry.text);
    const pageNumW = doc.getTextWidth(pageText);
    const dotsStart = margin + textW + 2;
    const dotsEnd = pageW - margin - pageNumW - 2;
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.2);
    for (let dx = dotsStart; dx < dotsEnd; dx += 2) {
      doc.line(dx, tocY + 0.5, dx + 0.5, tocY + 0.5);
    }
    tocY += 6;
  });

  // ── FOOTERS ─────────────────────────────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 2; i <= totalPages; i++) { // Skip cover page
    doc.setPage(i);
    const footY = pageH - 8;
    doc.setDrawColor(0, 71, 107);
    doc.setLineWidth(0.6);
    doc.line(margin, footY - 3, pageW - margin, footY - 3);
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text(`${i}/${totalPages}`, pageW - margin, footY, { align: 'right' });
    doc.text('CONFIDENTIAL', pageW / 2, footY, { align: 'center' });
  }

  // ── DOWNLOAD ────────────────────────────────────────────────────────
  const filename = data.assessment_name
    ? `${L.reportFilenamePrefix}_Risk_Assessment_${data.assessment_name.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
    : `${L.reportFilenamePrefix}_Risk_Assessment_Report_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
}
